import { useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2, ArrowLeft, Facebook } from "lucide-react";
import { COMPANY, FB_REVIEW_URL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewPage() {
  const [params] = useSearchParams();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ name: "", comment: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please pick a star rating."); return; }
    if (!form.name || !form.comment) { toast.error("Please add your name and a short comment."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { name: form.name, rating, comment: form.comment, booking_id: params.get("b") || "" });
      setDone(true);
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="reviewpage-home" className="inline-flex items-center gap-2 text-white/50 hover:text-[#F2B705] text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
          <div className="font-display font-extrabold text-2xl">AutoHire <span className="text-gold-gradient">STL</span></div>
        </div>

        <div className="mt-8 bg-[#0C0C0C] border border-white/10 rounded-2xl p-7" data-testid="reviewpage-card">
          {done ? (
            <div className="text-center py-6" data-testid="reviewpage-success">
              <CheckCircle2 className="text-[#F2B705] mx-auto" size={48} />
              <h1 className="font-display font-black text-2xl mt-4">Thank You! 🙏</h1>
              <p className="text-white/60 mt-2">Your review was submitted and will appear on our site once approved. Mind sharing it on Facebook too?</p>
              <a href={FB_REVIEW_URL} target="_blank" rel="noreferrer" data-testid="reviewpage-fb-success"
                className="inline-flex items-center gap-2 mt-6 bg-[#1877F2] text-white font-bold px-6 py-3 rounded-md hover:opacity-90 transition-opacity">
                <Facebook size={18} /> Review us on Facebook
              </a>
            </div>
          ) : (
            <>
              <h1 className="font-display font-black text-3xl">Leave a Review</h1>
              <p className="text-white/55 mt-2">How was your experience with Auto-Hire STL? Your feedback means the world to us.</p>

              <form onSubmit={submit} className="mt-6 space-y-6" data-testid="reviewpage-form">
                <div className="flex justify-center gap-2" data-testid="reviewpage-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" data-testid={`reviewpage-star-${n}`}
                      onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                      className="transition-transform hover:scale-110">
                      <Star size={36} className={(hover || rating) >= n ? "text-[#F2B705] fill-[#F2B705]" : "text-white/25"} />
                    </button>
                  ))}
                </div>
                <input data-testid="reviewpage-name" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 rounded-none placeholder:text-white/30" placeholder="Your name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <textarea data-testid="reviewpage-comment" rows={4} className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 rounded-none placeholder:text-white/30" placeholder="Tell us about your rental... *" value={form.comment} onChange={(e) => set("comment", e.target.value)} />
                <button data-testid="reviewpage-submit" type="submit" disabled={loading}
                  className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading && <Loader2 className="animate-spin" size={18} />} {loading ? "Submitting..." : "Submit Review"}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-white/10 text-center">
                <p className="text-white/45 text-sm mb-3">Prefer Facebook? Leave us a recommendation there:</p>
                <a href={FB_REVIEW_URL} target="_blank" rel="noreferrer" data-testid="reviewpage-fb"
                  className="inline-flex items-center gap-2 border border-[#1877F2]/50 text-[#4a9eff] font-semibold px-5 py-2.5 rounded-md hover:bg-[#1877F2] hover:text-white transition-colors">
                  <Facebook size={17} /> Review on Facebook
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
